import { useEffect, useState } from "react";
import { api, formatErr, inr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileSpreadsheet, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function GstReports() {
  const [tab, setTab] = useState("gstr1");
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const pg2 = usePagination(data?.rows || [], data);
  const pg = usePagination(data?.rows || [], data);

  useEffect(() => {
    setData(null);
    const path = tab === "gstr1" ? "gstr1" : tab === "gstr3b" ? "gstr3b" : "hsn-summary";
    api.get(`/reports/${path}?from_date=${from}&to_date=${to}`).then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
  }, [tab, from, to]);

  const exportCsv = () => {
    if (tab !== "gstr1") { window.print(); return; }
    const url = `${API}/reports/gstr1.csv?from_date=${from}&to_date=${to}`;
    const token = localStorage.getItem("erp_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = `gstr1_${from}_${to}.csv`; a.click();
      });
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><FileSpreadsheet className="w-3 h-3"/> Accounts · Statutory returns</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">GST Reports</h1>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From</Label><Input data-testid="from-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">To</Label><Input data-testid="to-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
          <Button data-testid="export-btn" onClick={exportCsv} variant="outline" className="rounded-sm h-10 text-xs uppercase tracking-wider">
            {tab === "gstr1" ? <><Download className="w-3 h-3 mr-1"/> CSV</> : <><Printer className="w-3 h-3 mr-1"/> Print</>}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-border">
        {[["gstr1", "GSTR-1 (Outward)"], ["gstr3b", "GSTR-3B (Summary)"], ["hsn", "HSN-wise Sales"]].map(([k, l]) => (
          <button key={k} data-testid={`gtab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${tab === k ? "text-primary border-b-2 border-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      <div id="gst-print">
      {tab === "gstr1" && data && (
        <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="border border-border bg-card p-3 rounded-sm"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Taxable Value</div><div className="font-display font-black text-xl mt-1">{inr(data.totals.taxable)}</div></div>
            <div className="border border-border bg-card p-3 rounded-sm"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">CGST</div><div className="font-display font-black text-xl mt-1">{inr(data.totals.cgst)}</div></div>
            <div className="border border-border bg-card p-3 rounded-sm"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">SGST</div><div className="font-display font-black text-xl mt-1">{inr(data.totals.sgst)}</div></div>
            <div className="border border-primary/40 bg-primary/5 p-3 rounded-sm"><div className="text-[10px] uppercase tracking-[0.2em] text-primary">Invoice Total</div><div className="font-display font-black text-xl text-primary mt-1">{inr(data.totals.total)}</div></div>
          </div>
          <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Invoice #</th><th className="text-left p-3">Party</th><th className="text-right p-3">Taxable</th><th className="text-right p-3">CGST</th><th className="text-right p-3">SGST</th><th className="text-right p-3">Total</th></tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No outward supplies</td></tr>}
                {pg.pageItems.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-3 font-mono text-xs">{r.date}</td>
                    <td className="p-3 font-mono text-xs text-primary uppercase">{r.invoice_no}</td>
                    <td className="p-3 text-xs">{r.party_name}</td>
                    <td className="p-3 text-right font-mono text-xs">{inr(r.taxable)}</td>
                    <td className="p-3 text-right font-mono text-xs">{inr(r.cgst)}</td>
                    <td className="p-3 text-right font-mono text-xs">{inr(r.sgst)}</td>
                    <td className="p-3 text-right font-mono">{inr(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationBar pg={pg}/>
          </div>
        </>
      )}

      {tab === "gstr3b" && data && (
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-border bg-card rounded-sm p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Outward Supplies (Sales)</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Taxable</span><span className="font-mono">{inr(data.outward.taxable)}</span></div>
              <div className="flex justify-between"><span>CGST</span><span className="font-mono">{inr(data.outward.cgst)}</span></div>
              <div className="flex justify-between"><span>SGST</span><span className="font-mono">{inr(data.outward.sgst)}</span></div>
            </div>
          </div>
          <div className="border border-border bg-card rounded-sm p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Inward Supplies (Purchases · ITC)</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Taxable</span><span className="font-mono">{inr(data.inward.taxable)}</span></div>
              <div className="flex justify-between"><span>CGST</span><span className="font-mono">{inr(data.inward.cgst)}</span></div>
              <div className="flex justify-between"><span>SGST</span><span className="font-mono">{inr(data.inward.sgst)}</span></div>
            </div>
          </div>
          <div className="col-span-2 border border-primary/40 bg-primary/5 rounded-sm p-5 flex justify-between items-center">
            <div className="text-xs uppercase tracking-[0.2em] text-primary">Net GST Payable</div>
            <div className={`font-display font-black text-3xl ${data.net_gst_payable >= 0 ? "text-primary" : "text-green-500"}`} data-testid="net-gst">{inr(data.net_gst_payable)}</div>
          </div>
        </div>
      )}

      {tab === "hsn" && data && (
        <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr><th className="text-left p-3">HSN</th><th className="text-right p-3">Qty</th><th className="text-right p-3">Taxable</th><th className="text-right p-3">CGST</th><th className="text-right p-3">SGST</th><th className="text-right p-3">Total</th></tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No sales for HSN summary</td></tr>}
              {pg2.pageItems.map((r, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="p-3 font-mono text-xs text-primary">{r.hsn}</td>
                  <td className="p-3 text-right font-mono text-xs">{r.qty}</td>
                  <td className="p-3 text-right font-mono text-xs">{inr(r.taxable)}</td>
                  <td className="p-3 text-right font-mono text-xs">{inr(r.cgst)}</td>
                  <td className="p-3 text-right font-mono text-xs">{inr(r.sgst)}</td>
                  <td className="p-3 text-right font-mono">{inr(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationBar pg={pg2} testid="pagination-2"/>
        </div>
      )}
      </div>
    </div>
  );
}
